import _ from 'lodash';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {visitor, type AstView} from 'rendition-core';

import {useKeyBind} from 'Hooks/useKeyBind';

import PreviewContext from 'Contexts/PreviewContext';
import {useNodeChildrenIds} from 'Hooks/AstHooks/useNodeChildren';
import {DocumentPreviewer} from '../RichPreview/DocumentPreviewer';
import {Resizer} from './Resizer';

import {AbsoluteEditHandles} from 'Components/AbsolutePositionHandles/AbsoluteEditHandles';
import HighlighterContextProvider from 'Contexts/HighlighterContext';
import {NodeCallbackContextProvider} from 'Contexts/NodeCallBackContext';
import {RightClickContextProvider} from 'Contexts/PreviewContext/RightClickMenuContext';
import {WebflowControls} from 'Views/Preview/useWebflowJSON';
import {GroupingTree} from './LayersPanel/GroupingTree';

import {useHighlighterContext} from 'Contexts/HighlighterContext';
import {useLoadFonts} from 'utils/loadFonts';
import {useParams} from 'react-router-dom';
import {useHoverHighlight} from './Hooks/UseHoverHighlight';
import useHotkeys from './useHotkeys';
import {useRegroupingStep} from './useRegroupingStep';
import {useRightClickMenuOptions} from './useRightClickMenuOptions';
import {useRegrouping} from './useRegrouping';
import {useGptRename} from 'Hooks/AstHooks/useGptRename';
import {ViewContextProvider, useViewContext} from 'Contexts/ViewContext';
import {useProductContext} from 'Contexts/ProductContext';
import {CopyTailwindControls} from './CopyTailwindControls';
import {RenditionTutorial} from './RenditionTutorial';
import {RightRail} from './RightRail';
import {UserMenu} from './UserMenu';

interface Props {
    view: AstView;
    webflowControls: WebflowControls;
    saveStatus: 'unsaved' | 'saved' | 'saving';
    setView: (view: AstView) => void;
    onGetCode: () => void;
    saveView: () => void;
    setRecordHistory: React.Dispatch<React.SetStateAction<boolean>>;
}

export const Builder: React.FC<Props> = (props) => {
    return (
        <ViewContextProvider view={props.view} setView={props.setView}>
            <NodeCallbackContextProvider>
                <HighlighterContextProvider view={props.view}>
                    <BuilderCore {...props} />
                </HighlighterContextProvider>
            </NodeCallbackContextProvider>
        </ViewContextProvider>
    );
};

export const BuilderCore: React.FC<React.PropsWithChildren<Props>> = ({
    saveStatus,
    onGetCode,
    webflowControls,
    setRecordHistory,
}) => {
    const {view, setView, selectedNodes, selectedNodeIds, setSelectedNodeIds} = useViewContext();

    const {id} = useParams<{id: string}>();
    useLoadFonts(view);
    const {gptRename, gptRenameLoading} = useGptRename();

    const {clearHighlightByType, addNodeHighlight, showGroupHighlight} = useHighlighterContext();

    const shiftKeyDownRef = useRef(false);

    useKeyBind('shift', () => (shiftKeyDownRef.current = true), undefined, {eventType: 'keydown'});
    useKeyBind('shift', () => (shiftKeyDownRef.current = false), undefined, {eventType: 'keyup'});

    const onSelectNodes = useCallback(
        (nodeIds: string[]) => {
            if (shiftKeyDownRef.current) {
                const intersection = _.intersection(nodeIds, selectedNodeIds);
                if (intersection.length > 0) {
                    setSelectedNodeIds(_.difference(selectedNodeIds, nodeIds));
                } else {
                    setSelectedNodeIds(_.uniq([...nodeIds, ...selectedNodeIds]));
                }
            } else {
                setSelectedNodeIds(nodeIds);
            }
            setResizeId(null);
        },
        [selectedNodeIds, setSelectedNodeIds]
    );

    const [resizeId, setResizeId] = useState<null | string>(null);

    const {flexNodes, absolutePositionedNodes} = useNodeChildrenIds(view.tree);

    const rightClickMenuOptions = useRightClickMenuOptions(view, setView, setResizeId, selectedNodes, onSelectNodes);

    const {jumpToEditNextNode, loadingCode, doGetCode} = useRegroupingStep({
        view,
        flexNodes,
        absPositionNodes: absolutePositionedNodes,
        onGetCode,
    });

    const {groupNodes, removeNodeAndChildren} = useRegrouping(onSelectNodes);
    useKeyBind('meta+g', (e) => {
        e.preventDefault();
        groupNodes(selectedNodes);
    });
    useKeyBind('del', (e) => {
        e.preventDefault();
        removeNodeAndChildren(selectedNodes);
    });
    useKeyBind('backspace', (e) => {
        e.preventDefault();
        removeNodeAndChildren(selectedNodes);
    });

    // highlight user groups
    // highlight system groups

    useEffect(() => {
        clearHighlightByType('GroupingHighlight');
        clearHighlightByType('Selected');
        clearHighlightByType('Padding');
        clearHighlightByType('Absolute');
        clearHighlightByType('Regroup');

        if (selectedNodeIds.length === 1) {
            const id = selectedNodeIds[0];
            addNodeHighlight(id, 'Selected');
        } else if (selectedNodeIds.length > 1) {
            selectedNodeIds.forEach((id) => {
                addNodeHighlight(id, 'Regroup');
            });
        }

        if (showGroupHighlight) {
            visitor(view.tree, view.meta, (currentNode) => {
                if (currentNode.children.length < 2) {
                    return;
                }
                addNodeHighlight(currentNode.internalId, 'GroupingHighlight');
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, selectedNodeIds, showGroupHighlight]);

    // Detect when 'space' is pressed in
    const hoverHandlers = useHoverHighlight({rootId: view.tree.internalId});

    useHotkeys(view, selectedNodes);
    const {mode} = useProductContext();

    // This prevents reloading when highlight is triggered.
    const memoizedRightRail = useMemo(
        () => (
            <RightRail
                view={view}
                setView={setView}
                selectedNodes={selectedNodes}
                selectedNodeIds={selectedNodeIds}
                loadingCode={loadingCode}
                webflowControls={webflowControls}
                id={id}
                doGetCode={doGetCode}
                onSelectNodes={onSelectNodes}
            />
        ),
        [view, setView, selectedNodes, selectedNodeIds, loadingCode, webflowControls, id, doGetCode, onSelectNodes]
    );

    return (
        <div className="relative flex h-screen max-h-screen min-h-screen w-full flex-row items-stretch justify-between overflow-hidden bg-[#000]">
            <PreviewContext
                value={{
                    view,
                    translucentNodes: absolutePositionedNodes,
                    onNodeClick: (e: React.MouseEvent, id?: string) => {
                        // Prevent right click from selecting nodes unless none are selected
                        if (e.button !== 2 || selectedNodeIds.length === 0) {
                            if (id) {
                                onSelectNodes([id]);
                                return true;
                            } else {
                                onSelectNodes([]);
                            }
                            return false;
                        }
                        return true;
                    },
                    onNodeMouseEnter: hoverHandlers.onNodeMouseEnter,
                    onNodeMouseLeave: hoverHandlers.onNodeMouseLeave,
                    unclickableNodes: [],
                    setUnclickableNodes: () => {},
                    hiddenNodes: [],
                }}
            >
                <RenditionTutorial />
                <RightClickContextProvider
                    selectedNodeIds={selectedNodeIds}
                    rightClickMenuOptions={rightClickMenuOptions}
                >
                    <DocumentPreviewer
                        rendersSelectedNodeAsRoot
                        isolatedView={true}
                        onNodeSelection={onSelectNodes}
                        view={view}
                        areaSize={700}
                        resizable
                        rootNode={view.tree}
                        selectedNodes={selectedNodes}
                        overLayedChildren={
                            <>
                                <Resizer
                                    setRecordHistory={setRecordHistory}
                                    nodeId={resizeId}
                                    clearResize={() => setResizeId(null)}
                                />

                                {selectedNodes.length === 1 &&
                                    selectedNodes[0].inputLayoutAbstraction.absolutePosition && (
                                        <AbsoluteEditHandles node={selectedNodes[0]} view={view} setView={setView} />
                                    )}
                            </>
                        }
                    />
                    <UserMenu />
                    <GroupingTree
                        saveStatus={saveStatus}
                        view={view}
                        selectedNodeIds={selectedNodeIds}
                        setView={setView}
                        gptRenameLoading={gptRenameLoading}
                        gptRename={gptRename}
                        jumpToEditNextNode={jumpToEditNextNode}
                    />
                </RightClickContextProvider>
                {memoizedRightRail}
                {mode === 'Rendition_Code' && <CopyTailwindControls view={view} selectedId={selectedNodeIds[0]} />}
            </PreviewContext>
        </div>
    );
};
